import { ReactNode, useEffect, useState } from 'react';

import { Loader2 } from 'lucide-react';

import { cn } from '@client/lib';

interface AdvisorOnboardingVideoSlideProps {
  title: string;
  description: ReactNode;
  videoId: string;
  onVideoPlayed?: () => void;
}

export function AdvisorOnboardingVideoSlide({
  title,
  description,
  videoId,
  onVideoPlayed,
}: AdvisorOnboardingVideoSlideProps) {
  const videoUrl = `https://www.loom.com/embed/${videoId}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`;
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    function handleBlur() {
      if (document.activeElement?.tagName === 'IFRAME') {
        onVideoPlayed?.();
      }
    }

    window.addEventListener('blur', handleBlur);

    return () => window.removeEventListener('blur', handleBlur);
  }, [onVideoPlayed]);

  return (
    <div className="flex flex-col items-center">
      <h1 className="mb-4 text-center text-3xl font-bold text-gray-950 md:text-4xl">{title}</h1>
      <p className="max-w-3xl text-balance text-center text-base text-gray-600 md:text-lg">
        {description}
      </p>
      <div
        className={cn('relative mt-8 w-full overflow-hidden rounded-2xl bg-gray-200', {
          'animate-pulse': !isLoaded,
        })}
      >
        {!isLoaded && (
          <div className="absolute inset-0 grid size-full place-items-center">
            <div className="mx-auto space-y-4 text-center font-medium text-gray-950">
              <Loader2 className="mx-auto size-8 animate-spin" />
              <p>Loading Video</p>
            </div>
          </div>
        )}
        <div className="relative aspect-video w-full">
          <iframe
            src={videoUrl}
            allowFullScreen
            loading="lazy"
            allow="autoplay; fullscreen"
            className="absolute inset-0 size-full"
            onLoad={() => setIsLoaded(true)}
          />
        </div>
      </div>
    </div>
  );
}
